import React, { useState } from "react";
import {
  Pencil, Trash2, Upload, FileText, Loader2, Search, Save, X, MessageCircle,
  Image as ImageIcon, Camera, UserCheck, PencilLine, FilePlus2, History,
} from "lucide-react";
import { KategoriRows } from "@/components/KategoriRows";
import { api, STATUSES, KATEGORI, fmtDate } from "@/lib/apiClient";
import { KategoriPicker } from "@/components/KategoriPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const stamp = (iso) =>
  iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "";

export const JobOrderTable = ({ rows, role, onChanged, statusFilter, setStatusFilter, search, setSearch, kategoriFilter, setKategoriFilter }) => {
  const [edit, setEdit] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingPhotoId, setUploadingPhotoId] = useState(null);

  const canEditAll = role === "manager" || role === "staff" || role === "kumendan";
  const canDelete = role === "manager" || role === "kumendan";
  const allDone = (jo) =>
    (jo.items || []).length > 0 &&
    (jo.items || []).every((i) => ["SELESAI", "DITERIMA"].includes(i.status));

  const openWa = async (jo) => {
    try {
      const { data } = await api.get(`/job-orders/${jo.id}/wa-link`);
      window.open(data.link, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat pesan WhatsApp");
    }
  };

  const uploadProof = async (jo, file) => {
    if (!file) return;
    setUploadingId(jo.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/job-orders/${jo.id}/proof`, fd);
      toast.success("Bukti pengambilan terunggah");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah bukti");
    } finally {
      setUploadingId(null);
    }
  };

  const uploadPhoto = async (jo, file) => {
    if (!file) return;
    setUploadingPhotoId(jo.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/job-orders/${jo.id}/photo`, fd);
      toast.success("Foto JO terunggah");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah foto JO");
    } finally {
      setUploadingPhotoId(null);
    }
  };

  const viewFile = async (fileId) => {
    try {
      const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Gagal membuka file");
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/job-orders/${edit.id}`, {
        tanggal: edit.tanggal,
        jo_number: edit.jo_number,
        client_name: edit.client_name,
        client_phone: edit.client_phone,
        deskripsi: edit.deskripsi,
        kategori: edit.kategori || [],
        status: edit.status,
      });
      toast.success("Job Order diperbarui");
      setEdit(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (jo) => {
    if (!window.confirm(`Hapus Job Order ${jo.jo_number}?`)) return;
    try {
      await api.delete(`/job-orders/${jo.id}`);
      toast.success("Job Order dihapus");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <div className="space-y-6" data-testid="jo-table-wrap">
      <div className="glass rounded-2xl p-5 md:p-6 flex flex-wrap items-center gap-3 justify-between">
        <h3 className="font-display text-xl font-semibold">
          Daftar Job Order{" "}
          <span className="text-slate-500 text-sm font-normal">({rows.length})</span>
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="jo-search-input"
              placeholder="Cari no JO / client"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 pl-9 w-56 bg-slate-900/70 border-white/10"
            />
          </div>
          <select
            data-testid="jo-kategori-filter"
            value={kategoriFilter}
            onChange={(e) => setKategoriFilter(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Semua Kategori</option>
            {KATEGORI.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
          <select
            data-testid="jo-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Semua Status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="jo-empty">
          Belum ada job order pada periode ini.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {rows.map((jo) => {
          const done = (jo.items || []).filter((i) =>
            ["SELESAI", "DITERIMA"].includes(i.status)
          ).length;
          return (
            <div key={jo.id} className="glass rounded-2xl p-5 md:p-6" data-testid={`jo-card-${jo.jo_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <div className="font-display text-lg font-semibold text-slate-100">
                    JO {jo.jo_number}{" "}
                    <span className="text-xs font-normal text-slate-500">· {fmtDate(jo.tanggal)}</span>
                  </div>
                  <div className="text-sm text-slate-300 mt-0.5">{jo.client_name}</div>
                  <div className="text-xs text-slate-500">{jo.client_phone || "-"}</div>
                  {jo.deskripsi && (
                    <div className="text-xs text-slate-500 mt-1 max-w-md">{jo.deskripsi}</div>
                  )}
                  <button
                    data-testid={`jo-detail-btn-${jo.jo_number}`}
                    onClick={() => setDetail(jo)}
                    className="mt-2 text-[10px] uppercase tracking-wider text-slate-500 hover:text-blue-300 transition-colors duration-200"
                  >
                    detail
                  </button>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={jo.status} />
                    <span className="rounded-md border border-white/10 bg-slate-900/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                      {done}/{(jo.items || []).length} selesai
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid={`jo-wa-btn-${jo.jo_number}`}
                      onClick={() => openWa(jo)}
                      disabled={!allDone(jo)}
                      variant="outline"
                      title={allDone(jo) ? "Kirim pesan WhatsApp ke client" : "Aktif setelah semua kategori SELESAI/DITERIMA"}
                      className="h-9 w-9 p-0 rounded-lg border-emerald-400/25 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-30"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    {canEditAll && (
                      <Button
                        data-testid={`jo-edit-btn-${jo.jo_number}`}
                        onClick={() => setEdit({ ...jo })}
                        variant="outline"
                        className="h-9 w-9 p-0 rounded-lg border-white/10 bg-slate-800/60 hover:bg-blue-600/20"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        data-testid={`jo-delete-btn-${jo.jo_number}`}
                        onClick={() => remove(jo)}
                        variant="outline"
                        className="h-9 w-9 p-0 rounded-lg border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="mt-4 rounded-xl border border-white/5 bg-slate-950/50 p-3 space-y-1.5"
                data-testid={`jo-history-${jo.jo_number}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Riwayat aktivitas JO
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <FilePlus2 className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                  Entri oleh <span className="font-semibold text-blue-200">{jo.created_by || "-"}</span>
                  <span className="text-slate-500">{stamp(jo.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                  ACC <span className="font-semibold text-emerald-200">{jo.admin_status || "belum"}</span>
                  {jo.admin_by && (
                    <>
                      oleh <span className="font-semibold text-emerald-200">{jo.admin_by}</span>
                      <span className="text-slate-500">{stamp(jo.admin_at)}</span>
                    </>
                  )}
                </div>
                {(jo.edits || []).slice(-3).map((e) => (
                  <div key={`e-${e.at}-${e.by}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <PencilLine className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                    <span>
                      Diedit oleh <span className="font-semibold text-amber-200">{e.by}</span>{" "}
                      <span className="text-slate-500">{stamp(e.at)}</span>
                      {e.fields?.length > 0 && (
                        <span className="text-slate-500"> · {e.fields.join(", ")}</span>
                      )}
                    </span>
                  </div>
                ))}
                {(jo.item_logs || []).slice(-6).reverse().map((l) => (
                  <div key={`l-${l.at}-${l.kategori}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <History className="w-3.5 h-3.5 text-sky-300 shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold text-sky-200">{l.kategori}</span> →{" "}
                      <span className="font-semibold text-slate-100">{l.status}</span> oleh{" "}
                      <span className="font-semibold text-sky-200">{l.by}</span>{" "}
                      <span className="text-slate-500">{stamp(l.at)}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <KategoriRows jo={jo} role={role} onChanged={onChanged} />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-white/10 pt-4">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Foto JO</span>
                {jo.jo_photo_file_id ? (
                  <button
                    data-testid={`jo-view-photo-${jo.jo_number}`}
                    onClick={() => viewFile(jo.jo_photo_file_id)}
                    className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 transition-colors duration-200"
                  >
                    <ImageIcon className="w-4 h-4" /> Lihat
                  </button>
                ) : (
                  <span className="text-xs text-slate-600" data-testid={`jo-photo-empty-${jo.jo_number}`}>
                    Belum ada
                  </span>
                )}
                {canEditAll && (
                  <>
                    <label
                      data-testid={`jo-photo-upload-label-${jo.jo_number}`}
                      className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 cursor-pointer transition-colors duration-200"
                    >
                      {uploadingPhotoId === jo.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {jo.jo_photo_file_id ? "Ganti file" : "Upload file"}
                      <input
                        data-testid={`jo-photo-input-${jo.jo_number}`}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="hidden"
                        onChange={(e) => uploadPhoto(jo, e.target.files?.[0])}
                      />
                    </label>
                    <label
                      data-testid={`jo-photo-camera-${jo.jo_number}`}
                      className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 cursor-pointer transition-colors duration-200"
                    >
                      <Camera className="w-4 h-4" /> Kamera
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => uploadPhoto(jo, e.target.files?.[0])}
                      />
                    </label>
                  </>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-4">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Bukti ambil</span>
                {jo.proof_file_id ? (
                  <button
                    data-testid={`jo-view-proof-${jo.jo_number}`}
                    onClick={() => viewFile(jo.proof_file_id)}
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4" /> Lihat
                  </button>
                ) : (
                  <span className="text-xs text-slate-600">Belum ada</span>
                )}
                <label
                  data-testid={`jo-upload-label-${jo.jo_number}`}
                  className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 cursor-pointer transition-colors duration-200"
                >
                  {uploadingId === jo.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload file
                  <input
                    data-testid={`jo-upload-input-${jo.jo_number}`}
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => uploadProof(jo, e.target.files?.[0])}
                  />
                </label>
                <label
                  data-testid={`jo-proof-camera-${jo.jo_number}`}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 cursor-pointer transition-colors duration-200"
                >
                  <Camera className="w-4 h-4" /> Kamera
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => uploadProof(jo, e.target.files?.[0])}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="glass border-white/10 max-w-2xl" data-testid="jo-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Job Order</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ubah data job order lalu simpan.
            </DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid gap-4 md:grid-cols-2 pt-2">
              <Input data-testid="edit-date" type="date" value={edit.tanggal} onChange={(e) => setEdit({ ...edit, tanggal: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
              <Input data-testid="edit-jo-number" value={edit.jo_number} onChange={(e) => setEdit({ ...edit, jo_number: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
              <Input data-testid="edit-client" value={edit.client_name} onChange={(e) => setEdit({ ...edit, client_name: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
              <Input data-testid="edit-phone" value={edit.client_phone || ""} onChange={(e) => setEdit({ ...edit, client_phone: e.target.value })} className="h-11 bg-slate-900/70 border-white/10" />
              <select
                data-testid="edit-status"
                value={edit.status}
                onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 md:col-span-2"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <Textarea data-testid="edit-desc" rows={3} value={edit.deskripsi} onChange={(e) => setEdit({ ...edit, deskripsi: e.target.value })} className="md:col-span-2 bg-slate-900/70 border-white/10" />
              <div className="md:col-span-2">
                <KategoriPicker
                  value={edit.kategori || []}
                  onChange={(v) => setEdit({ ...edit, kategori: v })}
                  testIdPrefix="edit-kategori"
                />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <Button data-testid="edit-save-btn" onClick={saveEdit} disabled={busy} className="h-11 flex-1 bg-blue-600 hover:bg-blue-500">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Simpan</>}
                </Button>
                <Button data-testid="edit-cancel-btn" onClick={() => setEdit(null)} variant="outline" className="h-11 border-white/10 bg-slate-800/60">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="glass border-white/10" data-testid="jo-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Detail JO {detail?.jo_number}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Rincian job order dan riwayat statusnya.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 pt-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-slate-400">Tanggal</span>
                <span className="text-slate-200">{fmtDate(detail.tanggal)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-400">Client</span>
                <span className="text-slate-200 text-right">
                  {detail.client_name}
                  <br />
                  <span className="text-xs text-slate-500">{detail.client_phone || "-"}</span>
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-400">Status</span>
                <StatusBadge status={detail.status} />
              </div>
              <div>
                <div className="text-slate-400 mb-2">Kategori</div>
                <div className="flex flex-wrap gap-1.5" data-testid="detail-kategori">
                  {(detail.items || []).length === 0 ? (
                    <span className="text-slate-500 text-xs">-</span>
                  ) : (
                    (detail.items || []).map((i) => (
                      <span
                        key={i.kategori}
                        className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200"
                      >
                        {i.kategori} · {i.status}
                      </span>
                    ))
                  )}
                </div>
              </div>
              <div>
                <div className="text-slate-400 mb-1">Deskripsi</div>
                <div className="text-slate-200">{detail.deskripsi}</div>
              </div>
              {(detail.details || []).length > 0 && (
                <div>
                  <div className="text-slate-400 mb-2">Detail Order</div>
                  <div className="overflow-x-auto rounded-lg border border-white/10">
                    <table className="w-full text-xs" data-testid="detail-order-table">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
                          <th className="px-2 py-2">Deskripsi</th>
                          <th className="px-2 py-2">Jenis</th>
                          <th className="px-2 py-2">Kategori</th>
                          <th className="px-2 py-2">P</th>
                          <th className="px-2 py-2">L</th>
                          <th className="px-2 py-2">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.details.map((d, i) => (
                          <tr key={`${d.kategori}-${i}`} className="border-t border-white/5 text-slate-200">
                            <td className="px-2 py-2">{d.deskripsi || "-"}</td>
                            <td className="px-2 py-2">{d.jenis_order || "-"}</td>
                            <td className="px-2 py-2 text-blue-200">{d.kategori}</td>
                            <td className="px-2 py-2">{d.panjang || "-"}</td>
                            <td className="px-2 py-2">{d.lebar || "-"}</td>
                            <td className="px-2 py-2">{d.qty || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {detail.jo_photo_file_id && (
                <Button
                  data-testid="detail-view-photo"
                  onClick={() => viewFile(detail.jo_photo_file_id)}
                  variant="outline"
                  className="w-full h-11 border-white/10 bg-slate-800/60 hover:bg-blue-600/20"
                >
                  <ImageIcon className="w-4 h-4 mr-2" /> Lihat Foto JO
                </Button>
              )}
              {detail.proof_file_id && (
                <Button
                  data-testid="detail-view-proof"
                  onClick={() => viewFile(detail.proof_file_id)}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-500"
                >
                  <FileText className="w-4 h-4 mr-2" /> Lihat Bukti Pengambilan
                </Button>
              )}
              {detail.history?.length > 0 && (
                <div className="border-t border-white/10 pt-4">
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-400 mb-3">Riwayat</div>
                  <ul className="space-y-2">
                    {detail.history.map((h) => (
                      <li key={`${h.at}-${h.status}`} className="flex justify-between gap-3 text-xs">
                        <span className="text-slate-200 font-medium">{h.status}</span>
                        <span className="text-slate-500">
                          {new Date(h.at).toLocaleString("id-ID")} · {h.by}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
